/**
 * Shared EUR-vector FX triangulation (LR-493 review CU4).
 *
 * Both FX sources store rates as units-of-ccy-per-1-EUR, so one vector covers every pair:
 *   rate(from → to) = eur[to] / eur[from]
 * This is the PURE math, extracted so `currency-service` (live + fallback spot) and `cob-fx-service`
 * (COB T-1) share it WITHOUT sharing their deliberately-separate caches, fallbacks, or provenance —
 * see the header in `cob-fx-service.ts` on why the two feeds must not mix.
 */

/** Units of `ccy` per 1 EUR from an EUR-based vector (EUR is trivially 1); `undefined` when the
 *  table is absent or doesn't list the currency. */
export const eurRateOf = (
  ccy: string,
  table: Record<string, number> | null | undefined
): number | undefined => (table ? (ccy === 'EUR' ? 1 : table[ccy]) : undefined);

/** The rate to convert 1 `from` into `to` via the EUR vector, or `undefined` when either leg is
 *  missing from `table`. Same-currency pairs are trivially 1 (independent of the table). */
export const eurTriangulate = (
  from: string,
  to: string,
  table: Record<string, number> | null | undefined
): number | undefined => {
  const f = from.toUpperCase();
  const t = to.toUpperCase();
  if (f === t) return 1;
  const ef = eurRateOf(f, table);
  const et = eurRateOf(t, table);
  if (!ef || !et) return undefined;
  return et / ef;
};
